import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { el, enUS } from 'date-fns/locale';
import {
  Calendar, Clock, Users, Video, Plus, Edit, Trash2, 
  ExternalLink, Loader2, DollarSign, MapPin, Copy
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import ImageUpload from '../../components/ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const adminInputClass =
  "w-full bg-misel-card border border-white/15 text-white placeholder:text-gray-500 rounded-sm px-3 py-2 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40 disabled:opacity-60 disabled:cursor-not-allowed";
const adminTextareaClass =
  "w-full min-h-[90px] bg-misel-card border border-white/15 text-white placeholder:text-gray-500 rounded-sm px-3 py-2 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40 disabled:opacity-60 disabled:cursor-not-allowed";
const adminSelectTriggerClass =
  "w-full bg-misel-card border border-white/15 text-white rounded-sm px-3 py-2 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40 disabled:opacity-60 disabled:cursor-not-allowed";
const adminSelectContentClass =
  "z-[9999] bg-[#111111] border border-white/15 text-white shadow-xl";
const adminSelectItemClass =
  "text-white focus:bg-primary/20 focus:text-white";
const adminFileInputClass =
  "text-xs text-gray-300 file:bg-misel-card file:border file:border-white/15 file:text-white file:rounded-sm file:px-3 file:py-1 file:mr-3 hover:file:border-primary";

const arrayToLines = (value) => Array.isArray(value) ? value.join('\n') : (value || '');
const linesToArray = (value) => (value || '')
  .split('\n')
  .map((item) => item.trim())
  .filter(Boolean);

export default function SeminarsManagement() {
  const { i18n } = useTranslation();
  const [seminars, setSeminars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSeminar, setEditingSeminar] = useState(null);
  const [creating, setCreating] = useState(false);
  const [creatingZoom, setCreatingZoom] = useState(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [sendingZoomDetails, setSendingZoomDetails] = useState(false);
  const [attendees, setAttendees] = useState([]);
  const [attendeesLoading, setAttendeesLoading] = useState(false);
  const [recipientMode, setRecipientMode] = useState('all');
  const [selectedAttendeeUserId, setSelectedAttendeeUserId] = useState('');
  const token = localStorage.getItem('token');

  const [form, setForm] = useState({
    title_el: '',
    title_en: '',
    description_el: '',
    description_en: '',
    short_description: '',
    full_description: '',
    what_you_will_learn: '',
    who_is_it_for: '',
    includes: '',
    outcome: '',
    instructor_bio: '',
    cta_label: '',
    instructor: '',
    start_at: '',
    timezone: 'Europe/Athens',
    duration_minutes: 60,
    price: 0,
    currency: 'EUR',
    capacity: 50,
    status: 'draft',
    is_free: false,
    image_url: '',
    cover_image_url: '',
    gallery_image_urls: '',
    instructor_image_url: '',
    promo_image_url: '',
    video_url: '',
    media_type: 'image'
  });

  const fetchSeminars = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/seminars`, { headers });
      setSeminars(res.data);
    } catch (e) {
      toast.error('Failed to load seminars');
      console.error('Fetch seminars error:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendees = async (seminarId) => {
    if (!seminarId) return;
    setAttendeesLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/seminars/${seminarId}/attendees`, { headers });
      setAttendees(res.data || []);
    } catch (e) {
      setAttendees([]);
      toast.error(i18n.language === 'el' ? 'Αποτυχία φόρτωσης συμμετεχόντων' : 'Failed to load attendees');
    } finally {
      setAttendeesLoading(false);
    }
  };

  const handleUploadMedia = async (file, mediaKind = 'image') => {
    if (!file) return;
    setUploadingMedia(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const data = new FormData();
      data.append('file', file);
      data.append('media_kind', mediaKind);

      const res = await axios.post(`${API}/admin/seminars/upload-media`, data, { headers });
      const mediaUrl = res.data?.url || '';
      if (mediaKind === 'video') {
        setForm((prev) => ({ ...prev, video_url: mediaUrl, media_type: 'video' }));
      } else {
        setForm((prev) => ({ ...prev, cover_image_url: mediaUrl, image_url: mediaUrl, media_type: prev.media_type || 'image' }));
      }

      toast.success(i18n.language === 'el' ? 'Το media ανέβηκε επιτυχώς' : 'Media uploaded successfully');
    } catch (e) {
      toast.error(e.response?.data?.detail || (i18n.language === 'el' ? 'Αποτυχία upload media' : 'Media upload failed'));
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleSendZoomDetails = async () => {
    if (!editingSeminar?.id) return;
    if (recipientMode === 'user' && !selectedAttendeeUserId) {
      toast.error(i18n.language === 'el' ? 'Επίλεξε συμμετέχοντα' : 'Select attendee');
      return;
    }

    setSendingZoomDetails(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        recipient_mode: recipientMode,
        user_id: recipientMode === 'user' ? selectedAttendeeUserId : null,
      };
      const res = await axios.post(`${API}/admin/seminars/${editingSeminar.id}/send-zoom-details`, payload, { headers });
      const data = res.data || {};
      toast.success(
        i18n.language === 'el'
          ? `Αποστολή ολοκληρώθηκε (Email: ${data.sent_email || 0}, SMS: ${data.sent_sms || 0})`
          : `Delivery complete (Email: ${data.sent_email || 0}, SMS: ${data.sent_sms || 0})`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || (i18n.language === 'el' ? 'Αποτυχία αποστολής Zoom στοιχείων' : 'Failed to send Zoom details'));
    } finally {
      setSendingZoomDetails(false);
    }
  };

  useEffect(() => {
    fetchSeminars();
  }, []);

  const openCreateDialog = () => {
    setEditingSeminar(null);
    setAttendees([]);
    setRecipientMode('all');
    setSelectedAttendeeUserId('');
    setForm({
      title_el: '',
      title_en: '',
      description_el: '',
      description_en: '',
      short_description: '',
      full_description: '',
      what_you_will_learn: '',
      who_is_it_for: '',
      includes: '',
      outcome: '',
      instructor_bio: '',
      cta_label: '',
      instructor: '',
      start_at: '',
      timezone: 'Europe/Athens',
      duration_minutes: 60,
      price: 0,
      currency: 'EUR',
      capacity: 50,
      status: 'draft',
      is_free: false,
      image_url: '',
      cover_image_url: '',
      gallery_image_urls: '',
      instructor_image_url: '',
      promo_image_url: '',
      video_url: '',
      media_type: 'image'
    });
    setDialogOpen(true);
  };

  const openEditDialog = (seminar) => {
    setEditingSeminar(seminar);
    setRecipientMode('all');
    setSelectedAttendeeUserId('');
    setForm({
      title_el: seminar.title_el || '',
      title_en: seminar.title_en || '',
      description_el: seminar.description_el || '',
      description_en: seminar.description_en || '',
      short_description: seminar.short_description || '',
      full_description: seminar.full_description || '',
      what_you_will_learn: arrayToLines(seminar.what_you_will_learn),
      who_is_it_for: seminar.who_is_it_for || '',
      includes: arrayToLines(seminar.includes),
      outcome: seminar.outcome || '',
      instructor_bio: seminar.instructor_bio || '',
      cta_label: seminar.cta_label || '',
      instructor: seminar.instructor || '',
      start_at: seminar.start_at ? seminar.start_at.slice(0, 16) : '',
      timezone: seminar.timezone || 'Europe/Athens',
      duration_minutes: seminar.duration_minutes || 60,
      price: seminar.price || 0,
      currency: seminar.currency || 'EUR',
      capacity: seminar.capacity || 50,
      status: seminar.status || 'draft',
      is_free: seminar.is_free || false,
      image_url: seminar.image_url || '',
      cover_image_url: seminar.cover_image_url || seminar.image_url || '',
      gallery_image_urls: arrayToLines(seminar.gallery_image_urls),
      instructor_image_url: seminar.instructor_image_url || '',
      promo_image_url: seminar.promo_image_url || '',
      video_url: seminar.video_url || '',
      media_type: seminar.media_type || (seminar.video_url ? 'video' : 'image')
    });
    fetchAttendees(seminar.id);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title_el || !form.start_at) {
      toast.error(i18n.language === 'el' ? 'Συμπληρώστε τίτλο και ημερομηνία' : 'Fill title and date');
      return;
    }

    setCreating(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const seminarPayload = {
        ...form,
        what_you_will_learn: linesToArray(form.what_you_will_learn),
        includes: linesToArray(form.includes),
        gallery_image_urls: linesToArray(form.gallery_image_urls),
        image_url: form.image_url || form.cover_image_url,
        cover_image_url: form.cover_image_url || form.image_url
      };

      if (editingSeminar) {
        await axios.put(`${API}/admin/seminars/${editingSeminar.id}`, seminarPayload, { headers });
        toast.success(i18n.language === 'el' ? 'Το σεμινάριο ενημερώθηκε' : 'Seminar updated');
      } else {
        await axios.post(`${API}/admin/seminars`, seminarPayload, { headers });
        toast.success(i18n.language === 'el' ? 'Το σεμινάριο δημιουργήθηκε' : 'Seminar created');
      }
      
      setDialogOpen(false);
      fetchSeminars();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(i18n.language === 'el' ? 'Διαγραφή σεμιναρίου;' : 'Delete seminar?')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/seminars/${id}`, { headers });
      toast.success(i18n.language === 'el' ? 'Το σεμινάριο διαγράφηκε' : 'Seminar deleted');
      fetchSeminars();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const handleCreateZoom = async (seminar) => {
    if (seminar.zoom_meeting_id) {
      toast.info(i18n.language === 'el' ? 'Το Zoom meeting υπάρχει ήδη' : 'Zoom meeting already exists');
      return;
    }

    setCreatingZoom(seminar.id);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/admin/seminars/${seminar.id}/create-zoom-meeting`, {}, { headers });
      toast.success(i18n.language === 'el' ? 'Το Zoom meeting δημιουργήθηκε!' : 'Zoom meeting created!');
      fetchSeminars();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create Zoom meeting');
    } finally {
      setCreatingZoom(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-500/20 text-gray-400',
      published: 'bg-green-500/20 text-green-400',
      cancelled: 'bg-red-500/20 text-red-400',
      completed: 'bg-blue-500/20 text-blue-400'
    };
    const labels = {
      draft: i18n.language === 'el' ? 'Πρόχειρο' : 'Draft',
      published: i18n.language === 'el' ? 'Δημοσιευμένο' : 'Published',
      cancelled: i18n.language === 'el' ? 'Ακυρωμένο' : 'Cancelled',
      completed: i18n.language === 'el' ? 'Ολοκληρωμένο' : 'Completed'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs ${styles[status] || styles.draft}`}>
        {labels[status] || status}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return format(date, 'dd MMM yyyy, HH:mm', { locale: i18n.language === 'el' ? el : enUS });
    } catch {
      return dateStr;
    }
  };

  const copyZoomValue = async (value, successMessage) => {
    if (!value) {
      toast.error(i18n.language === 'el' ? 'Δεν υπάρχει διαθέσιμη τιμή' : 'No value available');
      return;
    }

    try {
      await navigator.clipboard.writeText(String(value));
      toast.success(successMessage);
    } catch {
      toast.error(i18n.language === 'el' ? 'Αποτυχία αντιγραφής' : 'Copy failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">
          {i18n.language === 'el' ? 'Διαχείριση Σεμιναρίων' : 'Seminars Management'}
        </h2>
        <Button data-testid="admin-seminars-create-button" onClick={openCreateDialog} className="bg-primary hover:bg-primary/90">
          <Plus size={16} className="mr-2" />
          {i18n.language === 'el' ? 'Νέο Σεμινάριο' : 'New Seminar'}
        </Button>
      </div>

      {/* Seminars List */}
      <div className="grid gap-4">
        {seminars.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {i18n.language === 'el' ? 'Δεν υπάρχουν σεμινάρια' : 'No seminars yet'}
          </div>
        ) : (
          seminars.map((seminar) => (
            <div key={seminar.id} className="bg-misel-card border border-white/10 rounded-sm p-4">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">
                      {seminar.title_el || seminar.title_en}
                    </h3>
                    {getStatusBadge(seminar.status)}
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      {formatDate(seminar.start_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      {seminar.duration_minutes} min
                    </span>
                    <span className="flex items-center gap-1">
                      <Users size={14} />
                      {seminar.tickets_sold}/{seminar.capacity}
                    </span>
                    <span className="flex items-center gap-1">
                      <DollarSign size={14} />
                      {seminar.is_free ? (i18n.language === 'el' ? 'Δωρεάν' : 'Free') : `€${seminar.price}`}
                    </span>
                  </div>
                  {seminar.instructor && (
                    <p className="text-sm text-gray-500 mt-1">
                      {i18n.language === 'el' ? 'Εκπαιδευτής' : 'Instructor'}: {seminar.instructor}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {seminar.zoom_meeting_id ? (
                    <a
                      href={seminar.zoom_meeting_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                    >
                      <Video size={14} />
                      Zoom
                      <ExternalLink size={12} />
                    </a>
                  ) : (
                    <Button
                      data-testid={`admin-seminar-create-zoom-${seminar.id}`}
                      size="sm"
                      variant="outline"
                      className="border-blue-500/50 text-blue-400"
                      onClick={() => handleCreateZoom(seminar)}
                      disabled={creatingZoom === seminar.id}
                    >
                      {creatingZoom === seminar.id ? (
                        <Loader2 size={14} className="animate-spin mr-1" />
                      ) : (
                        <Video size={14} className="mr-1" />
                      )}
                      {i18n.language === 'el' ? 'Δημιουργία Zoom' : 'Create Zoom'}
                    </Button>
                  )}
                  <Button
                    data-testid={`admin-seminar-edit-${seminar.id}`}
                    size="sm"
                    variant="outline"
                    className="border-white/20 text-white"
                    onClick={() => openEditDialog(seminar)}
                  >
                    <Edit size={14} />
                  </Button>
                  <Button
                    data-testid={`admin-seminar-delete-${seminar.id}`}
                    size="sm"
                    variant="outline"
                    className="border-red-500/50 text-red-400"
                    onClick={() => handleDelete(seminar.id)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-misel-card border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSeminar 
                ? (i18n.language === 'el' ? 'Επεξεργασία Σεμιναρίου' : 'Edit Seminar')
                : (i18n.language === 'el' ? 'Νέο Σεμινάριο' : 'New Seminar')
              }
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">
                  {i18n.language === 'el' ? 'Τίτλος (Ελληνικά)' : 'Title (Greek)'} *
                </label>
                <Input
                  data-testid="seminar-form-title-el-input"
                  value={form.title_el}
                  onChange={(e) => setForm({ ...form, title_el: e.target.value })}
                  className={adminInputClass}
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">
                  {i18n.language === 'el' ? 'Τίτλος (Αγγλικά)' : 'Title (English)'}
                </label>
                <Input
                  data-testid="seminar-form-title-en-input"
                  value={form.title_en}
                  onChange={(e) => setForm({ ...form, title_en: e.target.value })}
                  className={adminInputClass}
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-400 mb-1 block">
                {i18n.language === 'el' ? 'Περιγραφή (Ελληνικά)' : 'Description (Greek)'}
              </label>
              <Textarea
                data-testid="seminar-form-description-el-input"
                value={form.description_el}
                onChange={(e) => setForm({ ...form, description_el: e.target.value })}
                className={adminTextareaClass}
                rows={3}
              />
            </div>

            <div>
              <label className="text-sm text-gray-400 mb-1 block">
                {i18n.language === 'el' ? 'Εκπαιδευτής' : 'Instructor'}
              </label>
              <Input
                data-testid="seminar-form-instructor-input"
                value={form.instructor}
                onChange={(e) => setForm({ ...form, instructor: e.target.value })}
                className={adminInputClass}
              />
            </div>

            <div className="border border-white/10 rounded-sm p-4 space-y-4" data-testid="seminar-sales-copy-section">
              <h4 className="text-sm font-semibold text-white">
                {i18n.language === 'el' ? 'Sales copy' : 'Sales copy'}
              </h4>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">
                  {i18n.language === 'el' ? 'Σύντομη περιγραφή' : 'Short description'}
                </label>
                <Textarea
                  data-testid="seminar-form-short-description-input"
                  value={form.short_description}
                  onChange={(e) => setForm({ ...form, short_description: e.target.value })}
                  className={adminTextareaClass}
                  rows={2}
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">
                  {i18n.language === 'el' ? 'Πλήρης περιγραφή' : 'Full description'}
                </label>
                <Textarea
                  data-testid="seminar-form-full-description-input"
                  value={form.full_description}
                  onChange={(e) => setForm({ ...form, full_description: e.target.value })}
                  className={adminTextareaClass}
                  rows={4}
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">
                  {i18n.language === 'el' ? 'Τι θα μάθεις, ένα ανά γραμμή' : 'What you will learn, one per line'}
                </label>
                <Textarea
                  data-testid="seminar-form-what-you-will-learn-input"
                  value={form.what_you_will_learn}
                  onChange={(e) => setForm({ ...form, what_you_will_learn: e.target.value })}
                  className={adminTextareaClass}
                  rows={4}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">
                    {i18n.language === 'el' ? 'Σε ποιους απευθύνεται' : 'Who is it for'}
                  </label>
                  <Textarea
                    data-testid="seminar-form-who-is-it-for-input"
                    value={form.who_is_it_for}
                    onChange={(e) => setForm({ ...form, who_is_it_for: e.target.value })}
                    className={adminTextareaClass}
                    rows={3}
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">
                    {i18n.language === 'el' ? 'Περιλαμβάνει, ένα ανά γραμμή' : 'Includes, one per line'}
                  </label>
                  <Textarea
                    data-testid="seminar-form-includes-input"
                    value={form.includes}
                    onChange={(e) => setForm({ ...form, includes: e.target.value })}
                    className={adminTextareaClass}
                    rows={3}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">
                  {i18n.language === 'el' ? 'Αποτέλεσμα' : 'Outcome'}
                </label>
                <Textarea
                  data-testid="seminar-form-outcome-input"
                  value={form.outcome}
                  onChange={(e) => setForm({ ...form, outcome: e.target.value })}
                  className={adminTextareaClass}
                  rows={2}
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">
                  {i18n.language === 'el' ? 'Bio εκπαιδευτή' : 'Instructor bio'}
                </label>
                <Textarea
                  data-testid="seminar-form-instructor-bio-input"
                  value={form.instructor_bio}
                  onChange={(e) => setForm({ ...form, instructor_bio: e.target.value })}
                  className={adminTextareaClass}
                  rows={3}
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">
                  {i18n.language === 'el' ? 'Κείμενο κουμπιού' : 'CTA label'}
                </label>
                <Input
                  data-testid="seminar-form-cta-label-input"
                  value={form.cta_label}
                  onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
                  className={adminInputClass}
                  placeholder={i18n.language === 'el' ? 'Κλείσε Θέση' : 'Book Seat'}
                />
              </div>
            </div>

            <div className="border border-white/10 rounded-sm p-4 space-y-3" data-testid="seminar-media-config-panel">
              <h4 className="text-sm font-semibold text-white">
                {i18n.language === 'el' ? 'Προβολή Media Σεμιναρίου' : 'Seminar Media'}
              </h4>

              <div>
                <label className="text-sm text-gray-400 mb-1 block">
                  {i18n.language === 'el' ? 'Τύπος προβολής στο card/detail' : 'Card/detail media type'}
                </label>
                <Select value={form.media_type} onValueChange={(value) => setForm({ ...form, media_type: value })}>
                  <SelectTrigger data-testid="seminar-form-media-type-select" className={adminSelectTriggerClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={adminSelectContentClass}>
                    <SelectItem className={adminSelectItemClass} value="image">{i18n.language === 'el' ? 'Εικόνα' : 'Image'}</SelectItem>
                    <SelectItem className={adminSelectItemClass} value="video">{i18n.language === 'el' ? 'Βίντεο' : 'Video'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <ImageUpload
                value={form.cover_image_url || form.image_url}
                onChange={(url) => setForm({ ...form, cover_image_url: url, image_url: url })}
                token={token}
                onFileUpload={(file) => handleUploadMedia(file, 'image')}
                label={i18n.language === 'el' ? 'Φωτογραφία σεμιναρίου' : 'Seminar image'}
                placeholder={i18n.language === 'el' ? 'https://... ή upload εικόνα' : 'https://... or upload image'}
              />

              <div className="border border-white/10 rounded-sm p-4 space-y-4" data-testid="seminar-images-section">
                <h4 className="text-sm font-semibold text-white">
                  {i18n.language === 'el' ? 'Images' : 'Images'}
                </h4>
                {(form.cover_image_url || form.image_url) && (
                  <img
                    src={form.cover_image_url || form.image_url}
                    alt="Cover preview"
                    className="h-28 w-full max-w-sm rounded-sm object-cover border border-white/10"
                  />
                )}
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">
                    {i18n.language === 'el' ? 'Gallery image URLs, ένα ανά γραμμή' : 'Gallery image URLs, one per line'}
                  </label>
                  <Textarea
                    data-testid="seminar-form-gallery-image-urls-input"
                    value={form.gallery_image_urls}
                    onChange={(e) => setForm({ ...form, gallery_image_urls: e.target.value })}
                    className={adminTextareaClass}
                    rows={3}
                    placeholder="https://..."
                  />
                  {linesToArray(form.gallery_image_urls).length > 0 && (
                    <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {linesToArray(form.gallery_image_urls).map((url, index) => (
                        <img
                          key={`${url}-${index}`}
                          src={url}
                          alt={`Gallery preview ${index + 1}`}
                          className="h-16 w-full rounded-sm object-cover border border-white/10"
                        />
                      ))}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">
                      {i18n.language === 'el' ? 'Instructor image URL' : 'Instructor image URL'}
                    </label>
                    <Input
                      data-testid="seminar-form-instructor-image-url-input"
                      value={form.instructor_image_url}
                      onChange={(e) => setForm({ ...form, instructor_image_url: e.target.value })}
                      className={adminInputClass}
                      placeholder="https://..."
                    />
                    {form.instructor_image_url && (
                      <img
                        src={form.instructor_image_url}
                        alt="Instructor preview"
                        className="mt-3 h-20 w-20 rounded-sm object-cover border border-white/10"
                      />
                    )}
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">
                      {i18n.language === 'el' ? 'Promo image URL' : 'Promo image URL'}
                    </label>
                    <Input
                      data-testid="seminar-form-promo-image-url-input"
                      value={form.promo_image_url}
                      onChange={(e) => setForm({ ...form, promo_image_url: e.target.value })}
                      className={adminInputClass}
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-1 block">
                  {i18n.language === 'el' ? 'Βίντεο σεμιναρίου (URL ή upload)' : 'Seminar video (URL or upload)'}
                </label>
                <div className="flex flex-col md:flex-row gap-2">
                  <Input
                    data-testid="seminar-form-video-url-input"
                    value={form.video_url}
                    onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                    className={adminInputClass}
                    placeholder="https://.../preview.mp4"
                  />
                  <input
                    data-testid="seminar-form-video-upload-input"
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleUploadMedia(file, 'video');
                    }}
                    className={adminFileInputClass}
                  />
                </div>
                {uploadingMedia && (
                  <p className="text-xs text-amber-300 mt-1" data-testid="seminar-media-uploading-indicator">
                    {i18n.language === 'el' ? 'Ανέβασμα media...' : 'Uploading media...'}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">
                  {i18n.language === 'el' ? 'Ημερομηνία & Ώρα' : 'Date & Time'} *
                </label>
                <Input
                  data-testid="seminar-form-start-at-input"
                  type="datetime-local"
                  value={form.start_at}
                  onChange={(e) => setForm({ ...form, start_at: e.target.value })}
                  className={adminInputClass}
                  style={{ colorScheme: 'dark' }}
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">
                  {i18n.language === 'el' ? 'Ζώνη Ώρας' : 'Timezone'}
                </label>
                <Input
                  data-testid="seminar-form-timezone-input"
                  value={form.timezone}
                  onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                  className={adminInputClass}
                  placeholder="Europe/Athens"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-400 mb-1 block">
                {i18n.language === 'el' ? 'Διάρκεια (λεπτά)' : 'Duration (minutes)'}
              </label>
              <Input
                data-testid="seminar-form-duration-input"
                type="number"
                value={form.duration_minutes}
                onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })}
                className={adminInputClass}
              />
            </div>

            {editingSeminar?.zoom_meeting_id && (
              <div data-testid="seminar-zoom-details-panel" className="border border-blue-500/30 bg-blue-500/5 rounded-sm p-4 space-y-3">
                <h4 className="text-sm font-semibold text-blue-300">
                  {i18n.language === 'el' ? 'Στοιχεία Zoom (Κοινοποίηση)' : 'Zoom Details (Share)'}
                </h4>

                <div className="grid gap-3">
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Join URL</label>
                      <Input data-testid="seminar-zoom-join-url-field" value={editingSeminar.zoom_meeting_url || ''} readOnly className={adminInputClass} />
                    </div>
                    <Button
                      type="button"
                      data-testid="seminar-zoom-join-url-copy-button"
                      variant="outline"
                      className="border-blue-400/50 text-blue-300"
                      onClick={() => copyZoomValue(editingSeminar.zoom_meeting_url, i18n.language === 'el' ? 'Το Zoom URL αντιγράφηκε' : 'Zoom URL copied')}
                    >
                      <Copy size={14} className="mr-2" />
                      {i18n.language === 'el' ? 'Αντιγραφή' : 'Copy'}
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Meeting ID (Username)</label>
                      <Input data-testid="seminar-zoom-meeting-id-field" value={editingSeminar.zoom_meeting_id || ''} readOnly className={adminInputClass} />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Passcode</label>
                      <Input data-testid="seminar-zoom-passcode-field" value={editingSeminar.zoom_password || ''} readOnly className={adminInputClass} />
                    </div>
                  </div>

                  {(editingSeminar.zoom_start_url || editingSeminar.zoom_scheduled_start_at) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">Zoom Start URL</label>
                        <Input data-testid="seminar-zoom-start-url-field" value={editingSeminar.zoom_start_url || ''} readOnly className={adminInputClass} />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">Zoom Scheduled Start</label>
                        <Input data-testid="seminar-zoom-scheduled-start-field" value={editingSeminar.zoom_scheduled_start_at || ''} readOnly className={adminInputClass} />
                      </div>
                    </div>
                  )}

                  <div className="border border-white/10 rounded-sm p-3 space-y-2" data-testid="seminar-zoom-resend-panel">
                    <p className="text-xs text-gray-300">
                      {i18n.language === 'el'
                        ? 'Αποστολή Zoom στοιχείων ξανά (Email + SMS όπου υπάρχει κινητό)'
                        : 'Send Zoom details again (Email + SMS where phone exists)'}
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">{i18n.language === 'el' ? 'Παραλήπτες' : 'Recipients'}</label>
                        <Select value={recipientMode} onValueChange={(v) => setRecipientMode(v)}>
                          <SelectTrigger data-testid="seminar-zoom-resend-mode-select" className={adminSelectTriggerClass}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className={adminSelectContentClass}>
                            <SelectItem className={adminSelectItemClass} value="all">{i18n.language === 'el' ? 'Όλοι οι πληρωμένοι' : 'All paid attendees'}</SelectItem>
                            <SelectItem className={adminSelectItemClass} value="user">{i18n.language === 'el' ? 'Συγκεκριμένος συμμετέχων' : 'Specific attendee'}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {recipientMode === 'user' && (
                        <div>
                          <label className="text-xs text-gray-400 mb-1 block">{i18n.language === 'el' ? 'Συμμετέχων' : 'Attendee'}</label>
                          <Select value={selectedAttendeeUserId} onValueChange={(v) => setSelectedAttendeeUserId(v)}>
                            <SelectTrigger data-testid="seminar-zoom-resend-user-select" className={adminSelectTriggerClass}>
                              <SelectValue placeholder={attendeesLoading ? 'Loading...' : (i18n.language === 'el' ? 'Επέλεξε' : 'Select')} />
                            </SelectTrigger>
                            <SelectContent className={adminSelectContentClass}>
                              {(attendees || []).filter((att) => att.user_id).map((att) => (
                                <SelectItem className={adminSelectItemClass} key={att.ticket_id} value={att.user_id}>
                                  {att.name || att.email || att.user_id}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    <Button
                      type="button"
                      data-testid="seminar-zoom-resend-send-button"
                      variant="outline"
                      className="border-blue-400/50 text-blue-300"
                      disabled={sendingZoomDetails || attendeesLoading}
                      onClick={handleSendZoomDetails}
                    >
                      {sendingZoomDetails ? <Loader2 size={14} className="animate-spin mr-2" /> : <Video size={14} className="mr-2" />}
                      {i18n.language === 'el' ? 'Send Zoom details again' : 'Send Zoom details again'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">
                  {i18n.language === 'el' ? 'Τιμή (€)' : 'Price (€)'}
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
                  className={adminInputClass}
                  disabled={form.is_free}
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">
                  {i18n.language === 'el' ? 'Χωρητικότητα' : 'Capacity'}
                </label>
                <Input
                  type="number"
                  value={form.capacity}
                  onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 50 })}
                  className={adminInputClass}
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">
                  {i18n.language === 'el' ? 'Κατάσταση' : 'Status'}
                </label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className={adminSelectTriggerClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={adminSelectContentClass}>
                    <SelectItem className={adminSelectItemClass} value="draft">{i18n.language === 'el' ? 'Πρόχειρο' : 'Draft'}</SelectItem>
                    <SelectItem className={adminSelectItemClass} value="published">{i18n.language === 'el' ? 'Δημοσιευμένο' : 'Published'}</SelectItem>
                    <SelectItem className={adminSelectItemClass} value="cancelled">{i18n.language === 'el' ? 'Ακυρωμένο' : 'Cancelled'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                data-testid="seminar-form-is-free-checkbox"
                type="checkbox"
                id="is_free"
                checked={form.is_free}
                onChange={(e) => setForm({ ...form, is_free: e.target.checked, price: e.target.checked ? 0 : form.price })}
                className="accent-primary"
              />
              <label htmlFor="is_free" className="text-sm text-gray-400">
                {i18n.language === 'el' ? 'Δωρεάν σεμινάριο' : 'Free seminar'}
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button data-testid="seminar-form-cancel-button" variant="outline" onClick={() => setDialogOpen(false)} className="border-white/20 text-white">
                {i18n.language === 'el' ? 'Ακύρωση' : 'Cancel'}
              </Button>
              <Button data-testid="seminar-form-save-button" onClick={handleSave} className="bg-primary hover:bg-primary/90" disabled={creating}>
                {creating ? (
                  <Loader2 size={16} className="animate-spin mr-2" />
                ) : null}
                {i18n.language === 'el' ? 'Αποθήκευση' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
